import ClosableDialog from "./ClosableDialog";
import ResponsiveButtonWithTooltip from "../buttons/ResponsiveButtonWithTooltip";
import {TextDisplay} from "../data-display/TextDisplay";
import React, {useContext} from "react";
import {LanguageContext} from "../../../config/ContextConfig";

/**
 * Represents the properties required to manage unsaved data protection in a user interface.
 * This interface is designed to ensure users are prompted when they attempt to navigate away with unsaved changes.
 *
 * This mechanism is critical for preventing accidental data loss and enhancing user confidence while interacting with the application.
 *
 * @property isOpen Indicates whether the unsaved data protection modal is currently displayed. If `true`, the modal should be visible to the user.
 * @property onYesClick A callback function that is triggered when the user confirms their intent to navigate away or discard unsaved changes. This is typically used to safely proceed with the navigation.
 * @property onNoClick A callback function that is triggered when the user decides to stay on the current page and retain their unsaved changes. This is typically used to close the modal and preserve the current state.
 */
export interface UnsavedDataProtectionProps {
    isOpen: boolean
    onYesClick: () => void
    onNoClick: () => void
}

/**
 * Displays a confirmation dialog to alert the user about unsaved changes before navigating away or closing the current view.
 *
 * This component provides a modal dialog that prevents accidental loss of unsaved data by prompting the user to confirm their intent. It supports a "Yes" or "No" response to proceed or cancel the action.
 *
 * @param onYesClick - A callback function invoked when the user confirms by clicking the "Yes" button, indicating they want to proceed despite unsaved changes.
 * @param onNoClick - A callback function invoked when the user cancels by clicking the "No" button, indicating they want to stay on the current view and preserve their unsaved data.
 * @param isOpen - A boolean flag that determines whether the dialog is visible to the user. Set to `true` to display the dialog and `false` to hide it.
 *
 * @return React JSX.Element representing the rendered confirmation dialog.
 */
export function UnsavedDataProtection(
    {
        onYesClick,
        onNoClick,
        isOpen
    }: UnsavedDataProtectionProps
) {
    const language = useContext( LanguageContext );

    return (
        <>
            <ClosableDialog
                onCloseClick={ onNoClick }
                contentMinHeight={ 100 }
                isOpen={ isOpen }
                title={ language.common.unsavedDataWarning.title }
                dialogActionComponents={ (
                    <div className="flex items-center">
                        <ResponsiveButtonWithTooltip
                            variant="secondary"
                            tooltip={""}
                            text={ language.common.no }
                            onClick={ onNoClick }
                        />
                        <ResponsiveButtonWithTooltip
                            variant="secondary"
                            tooltip={""}
                            text={ language.common.yes }
                            onClick={ onYesClick }
                        />
                    </div>
                ) }
            >
                <TextDisplay variant="body1">
                    { language.common.unsavedDataWarning.helpText }
                </TextDisplay>

            </ClosableDialog>
        </>

    );
}